import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { ThemeProvider, createTheme, withStyles } from "@material-ui/core/styles";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Collapse from "@material-ui/core/Collapse";
import ListItem from "@material-ui/core/ListItem";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import MomentUtils from "@date-io/moment";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import NumericalTextField from "../../../material-components/Misc/NumericalTextField";
import { DeliveryType } from "../../../../enums/company";
import {
    updateAttribute,
    getUpdatedCPMandPOM,
    syncScheduleEndDateWithExpiration,
} from "../../../../actions/flowActions";
import { requestCompanies, setLoadingCompany } from "../../../../actions/userActions";
import { formatExpirationDate, parseDateToTimeZoneWithTime } from "../../../../helpers/parseDate";

// used to remove arrow icons from number text fields
// cant have those because we fail to update on click from 1 -> 0
// due to using both selects and text fields
const styles = {
    input: {
        "&[type=number]": {
            "-moz-appearance": "textfield",
        },
        "&::-webkit-outer-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
        "&::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
    },
};

const ExpandLess = <i className="material-icons">expand_less</i>;
const ExpandMore = <i className="material-icons">expand_more</i>;
const truncateToThreeDecimals = (value, currencySymbol = "$") => {
    // Ensure the value is a number and format it with up to 3 decimals
    const numericValue = parseFloat(value);
    let formattedValue = 0;
    if (!isNaN(numericValue)) {
        formattedValue = Math.floor(numericValue * 1000) / 1000; // Truncate to 3 decimals
    }
    return `${currencySymbol}${formattedValue}`;
};

const ExportTradeDeskTemplateFields = ({
    tapadDeliveryTypeId,
    destinationId,
    flowExportTradeDeskTemplateFields,
    canEdit,
    classes,
}) => {
    const dispatch = useDispatch();

    const defaultExpansionType = "HOUSEHOLD";

    // state
    const [templateRows, setTemplateRows] = useState([]);
    const [deliveryType, setDeliveryType] = useState();
    const [isOpenGlobalAssignment, setIsOpenGlobalAssignment] = useState(false);
    const [globalExpansionType, setGlobalExpansionType] = useState(defaultExpansionType);
    const [globalPercentOfMediaCost, setGlobalPercentOfMediaCost] = useState(0);
    const [globalCostPerMillion, setGlobalCostPerMillion] = useState(0);
    const [globalExpirationDate, setGlobalExpirationDate] = useState();
    const [refreshTable, setRefreshTable] = useState(0);
    const [sorting, setSorting] = useState([]);

    // props
    const companies = useSelector(state => state.companies.byId);
    const loadingCompanies = useSelector(state => state.vars.loadingCompanies);
    const flow = useSelector(state => state.flows.byId[state.selected.flow]);
    const timeZoneIana = useSelector(state => state.session.userSettings["time-zone-iana"]);
    const tapadDeliveryTypes = useSelector(state => state.vars.tapadDeliveryTypes);
    const enabledFeatures = useSelector(state => state.session.enabledFeatures);

    // Variables use to calcuate CPM and POM
    const flowItems = useSelector(state => state.flowItems.byId);
    const hasFeatureDisplayFieldRates = enabledFeatures.includes("display-field-rates");
    const hasScheduleTemplatesFeature = enabledFeatures.includes("schedule-templates");

    const renderTemplateRows = () => {
        let rows = [];

        // Set default parentElementId and ParentElementName
        let defaultParentElementId = "";
        let defaultParentElementName = "";

        if (deliveryType) {
            if (DeliveryType.ThirdPartyCustom == deliveryType.DeliveryType) {
                defaultParentElementId = deliveryType.ParentElementId;
                defaultParentElementName = deliveryType.ParentElementName;
            }
        }

        if (flowExportTradeDeskTemplateFields != null) {
            for (const template of flowExportTradeDeskTemplateFields) {
                // if the date is null, not showing a default date because it makes the user think it's good
                // only happens when saving an old flow with null ExpirationDate
                const formattedRunDate = template.ExpirationDate
                    ? hasScheduleTemplatesFeature
                        ? formatExpirationDate(template.ExpirationDate)
                        : new Date(parseDateToTimeZoneWithTime(template.ExpirationDate, timeZoneIana))
                    : null;

                if (deliveryType && DeliveryType.ThirdPartyCustom == deliveryType.DeliveryType) {
                    if (template.ParentElementId == null) {
                        dispatch(
                            updateAttribute(
                                "flowExportTradeDeskTemplateFields",
                                template.FlowExportTemplateId,
                                "ParentElementId",
                                defaultParentElementId
                            )
                        );
                    }

                    if (template.ParentElementName == null) {
                        dispatch(
                            updateAttribute(
                                "flowExportTradeDeskTemplateFields",
                                template.FlowExportTemplateId,
                                "ParentElementName",
                                defaultParentElementName
                            )
                        );
                    }
                }

                // Default Expansion Type
                if (!template.ExpansionType || template.ExpansionType == "") {
                    dispatch(
                        updateAttribute(
                            "flowExportTradeDeskTemplateFields",
                            template.FlowExportTemplateId,
                            "ExpansionType",
                            defaultExpansionType
                        )
                    );
                }

                if (deliveryType && DeliveryType.ThirdPartyCustom == deliveryType.DeliveryType) {
                    if (template.RateType != "Hybrid") {
                        handleRateTypeChange(template.FlowExportTemplateId, "Hybrid");
                    }
                }

                rows.push({
                    FlowExportTemplateId: template.FlowExportTemplateId,
                    ParentFlowItemId: template.ParentFlowItemId,
                    AudienceName: template.AudienceName || "",
                    Description: template.Description || "",
                    ExpansionType: template.ExpansionType || defaultExpansionType,
                    RateType: template.RateType || "CPM",
                    CostPerMillion: template.CostPerMillion || 0,
                    PercentOfMediaCost: template.PercentOfMediaCost || 0,
                    TTL: template.TTL || 0,
                    ExpirationDate: formattedRunDate,
                    ProviderElementId: template.ProviderElementId || "",
                    ParentElementId:
                        template.ParentElementId != null ? template.ParentElementId : defaultParentElementId,
                    ParentElementName:
                        template.ParentElementName != null ? template.ParentElementName : defaultParentElementName,
                    IsAutoGeneratedCPM: template.IsAutoGeneratedCPM,
                    IsAutoGeneratedPOM: template.IsAutoGeneratedPOM,
                });
            }
        }

        setTemplateRows(rows);
        setRefreshTable(refreshTable + 1);
    };

    // effects
    useEffect(() => {
        renderTemplateRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        destinationId,
        timeZoneIana,
        deliveryType,
        dispatch,
        flowExportTradeDeskTemplateFields,
        hasScheduleTemplatesFeature,
    ]);

    // effects

    useEffect(() => {
        if (!companies || (Object.keys(companies).length <= 0 && !loadingCompanies)) {
            dispatch(setLoadingCompany(true));
            dispatch(requestCompanies());
        }
    }, [dispatch, companies, loadingCompanies]);

    useEffect(() => {
        const thisDeliveryType = tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);

        if (deliveryType && thisDeliveryType?.TapadDeliveryTypeId != deliveryType.TapadDeliveryTypeId) {
            // addMissingFlowOfferMerges
            for (let i = 0; i < flowExportTradeDeskTemplateFields.length; i++) {
                const flowItemsArray = Object.values(flowItems);
                const FlowItem = flowItemsArray.find(
                    x => x.FlowItemId == flowExportTradeDeskTemplateFields[i].ParentFlowItemId
                );

                const newCPMValue = dispatch(
                    getUpdatedCPMandPOM(
                        FlowItem,
                        flowExportTradeDeskTemplateFields[i].FlowItemId,
                        flowExportTradeDeskTemplateFields[i].ParentFlowItemId,
                        hasFeatureDisplayFieldRates,
                        1,
                        true
                    )
                );
                flowExportTradeDeskTemplateFields[i].CostPerMillion = newCPMValue;
                const newPOMValue = dispatch(
                    getUpdatedCPMandPOM(
                        FlowItem,
                        flowExportTradeDeskTemplateFields[i].FlowItemId,
                        flowExportTradeDeskTemplateFields[i].ParentFlowItemId,
                        hasFeatureDisplayFieldRates,
                        2,
                        true
                    )
                );
                flowExportTradeDeskTemplateFields[i].PercentOfMediaCost = newPOMValue;
                flowExportTradeDeskTemplateFields[i].IsAutoGeneratedCPM = true;
                flowExportTradeDeskTemplateFields[i].IsAutoGeneratedPOM = true;
            }
            setTemplateRows(flowExportTradeDeskTemplateFields);
            renderTemplateRows();
            setRefreshTable(refreshTable + 1);
        }
        if (thisDeliveryType && flowExportTradeDeskTemplateFields.length > 0) {
            setDeliveryType(thisDeliveryType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tapadDeliveryTypeId, flowExportTradeDeskTemplateFields]);

    const selectAudienceName = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <TextField
                key={cellInfo.tableData.id + "-audience-name-" + cellInfo.FlowExportTemplateId}
                type="text"
                defaultValue={cellInfo.AudienceName}
                disabled={!canEdit || (flow && flow.IsLocked)}
                inputProps={{
                    style: { fontSize: "16px" },
                    maxLength: 100,
                }}
                onChange={e => {
                    rows[cellInfo.tableData.id].AudienceName = e.target.value;
                    setTemplateRows(rows);
                }}
                onBlur={e => handleChange(cellInfo.FlowExportTemplateId, "AudienceName", e)}
                fullWidth
            />
        );
        return column;
    };

    const selectDescription = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <TextField
                key={cellInfo.tableData.id + "-template-description-" + cellInfo.FlowExportTemplateId}
                type="text"
                defaultValue={cellInfo.Description}
                disabled={!canEdit || (flow && flow.IsLocked)}
                inputProps={{
                    style: { fontSize: "16px" },
                }}
                onChange={e => {
                    rows[cellInfo.tableData.id].Description = e.target.value;
                    setTemplateRows(rows);
                }}
                onBlur={e => handleChange(cellInfo.FlowExportTemplateId, "Description", e)}
                fullWidth
            />
        );
        return column;
    };

    const selectExpansionType = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <Select
                key={cellInfo.tableData.id + "expansion-type" + cellInfo.FlowExportTemplateId}
                style={{ fontSize: "14px", width: "100%" }}
                value={cellInfo.ExpansionType}
                onChange={e => {
                    rows[cellInfo.tableData.id].ExpansionType = e.target.value;
                    setTemplateRows(rows);
                    handleExpansionTypeChange(cellInfo.FlowExportTemplateId, e);
                }}
                disabled={!canEdit || (flow && flow.IsLocked)}
                displayEmpty
            >
                <MenuItem key={2} value={"HOUSEHOLD"}>
                    Household
                </MenuItem>
            </Select>
        );
        return column;
    };

    const selectExpirationDate = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <DateTimePicker
                    key={cellInfo.tableData.id + "-expiration-date-" + cellInfo.FlowExportTemplateId}
                    format={"MMM DD, YYYY HH:mm"}
                    value={cellInfo.ExpirationDate}
                    disabled={!canEdit || (flow && flow.IsLocked)}
                    onChange={e => {
                        const newDateTime = hasScheduleTemplatesFeature ? formatExpirationDate(e) : e?.utc().format();
                        rows[cellInfo.tableData.id].ExpirationDate = newDateTime;
                        setTemplateRows(rows);
                        handleDateTimeChange(cellInfo.FlowExportTemplateId, "ExpirationDate", newDateTime);
                        setRefreshTable(refreshTable + 1);
                    }}
                />
            </MuiPickersUtilsProvider>
        );
        return column;
    };

    const selectRateType = cellInfo => {
        if (DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType) {
            return "Hybrid";
        }

        const rows = [...templateRows];
        const column = (
            <Select
                key={cellInfo.tableData.id + "-rate-type-" + cellInfo.FlowExportTemplateId}
                style={{ fontSize: "14px", width: "100%" }}
                value={cellInfo.RateType}
                onChange={e => {
                    rows[cellInfo.tableData.id].RateType = e.target.value;
                    setTemplateRows(rows);
                    handleRateTypeChange(cellInfo.FlowExportTemplateId, e.target.value);
                }}
                disabled={!canEdit || (flow && flow.IsLocked)}
                displayEmpty
            >
                <MenuItem key={1} value={"CPM"}>
                    CPM
                </MenuItem>
                <MenuItem key={2} value={"PercentOfMediaCost"}>
                    % of Media Cost
                </MenuItem>
                <MenuItem key={3} value={"Hybrid"}>
                    Hybrid
                </MenuItem>
            </Select>
        );
        return column;
    };

    const getCellStyleCPM = (cellInfo, rowData) => {
        // const rows = [...templateRows];
        // var myRow = rows[cellInfo.FlowExportTemplateId];
        let isAutoGenerated = rowData.IsAutoGeneratedCPM;
        let cellstyleJSON;
        if (isAutoGenerated && hasFeatureDisplayFieldRates) {
            cellstyleJSON = {
                backgroundColor: "#87CEEB",
                color: "#OOOO",
            };
        } else {
            cellstyleJSON = {
                backgroundColor: "#FFFF",
                color: "#OOOO",
            };
        }

        return cellstyleJSON;
    };
    const getCellStylePOM = (cellInfo, rowData) => {
        // const rows = [...templateRows];
        // var myRow = rows[cellInfo.FlowExportTemplateId];
        let isAutoGenerated = rowData.IsAutoGeneratedPOM;
        let cellstyleJSON;
        if (isAutoGenerated && hasFeatureDisplayFieldRates) {
            cellstyleJSON = {
                backgroundColor: "#87CEEB",
                color: "#OOOO",
            };
        } else {
            cellstyleJSON = {
                backgroundColor: "#FFFF",
                color: "#OOOO",
            };
        }

        return cellstyleJSON;
    };

    const selectCostPerMillion = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <NumericalTextField
                key={cellInfo.tableData.id + "-CostPerMillion-" + cellInfo.FlowExportTemplateId}
                classes={classes}
                value={truncateToThreeDecimals(cellInfo.CostPerMillion)}
                disabled={!canEdit || (flow && flow.IsLocked)}
                numberType="currency3"
                onChange={e => {
                    rows[cellInfo.tableData.id].CostPerMillion = e.target.value.replace(/[^0-9.]/g, "");
                    rows[cellInfo.tableData.id].IsAutoGeneratedCPM = false;
                    setTemplateRows(rows);
                }}
                onBlur={e => {
                    handlePositiveValueChange(
                        cellInfo.tableData.id,
                        cellInfo.FlowExportTemplateId,
                        "CostPerMillion",
                        e
                    );
                }}
            />
        );
        return column;
    };

    const selectPercentOfMediaCost = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <NumericalTextField
                key={cellInfo.tableData.id + "-PercentOfMediaCost-" + cellInfo.FlowExportTemplateId}
                classes={classes}
                value={cellInfo.PercentOfMediaCost + "%"}
                disabled={!canEdit || (flow && flow.IsLocked)}
                numberType="percent"
                onChange={e => {
                    rows[cellInfo.tableData.id].PercentOfMediaCost = e.target.value.replace(/[^0-9.]/g, "");
                    rows[cellInfo.tableData.id].IsAutoGeneratedPOM = false;
                    setTemplateRows(rows);
                }}
                onBlur={e =>
                    handlePositiveValueChange(
                        cellInfo.tableData.id,
                        cellInfo.FlowExportTemplateId,
                        "PercentOfMediaCost",
                        e
                    )
                }
            />
        );
        return column;
    };

    const selectParentElementId = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <TextField
                key={cellInfo.tableData.id + "-template-parent-element-id-" + cellInfo.FlowExportTemplateId}
                type="text"
                defaultValue={cellInfo.ParentElementId}
                disabled={!canEdit || (flow && flow.IsLocked)}
                inputProps={{
                    style: { fontSize: "16px" },
                }}
                onChange={e => {
                    rows[cellInfo.tableData.id].ParentElementId = e.target.value;
                    setTemplateRows(rows);
                }}
                onBlur={e => handleChange(cellInfo.FlowExportTemplateId, "ParentElementId", e)}
            />
        );
        return column;
    };

    const selectParentElementName = cellInfo => {
        const rows = [...templateRows];
        const column = (
            <TextField
                key={cellInfo.tableData.id + "-template-parent-element-name-" + cellInfo.FlowExportTemplateId}
                type="text"
                defaultValue={cellInfo.ParentElementName}
                disabled={!canEdit || (flow && flow.IsLocked)}
                inputProps={{
                    style: { fontSize: "16px" },
                }}
                onChange={e => {
                    rows[cellInfo.tableData.id].ParentElementName = e.target.value;
                    setTemplateRows(rows);
                }}
                onBlur={e => handleChange(cellInfo.FlowExportTemplateId, "ParentElementName", e)}
            />
        );
        return column;
    };

    const handleChange = (TemplateId, attributeName, e) =>
        dispatch(updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, attributeName, e.target.value));

    const handleDateTimeChange = (TemplateId, attributeName, newDateTime) => {
        dispatch(updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, attributeName, newDateTime));
        if (attributeName == "ExpirationDate") {
            dispatch(syncScheduleEndDateWithExpiration(newDateTime));
        }
    };

    const handleGlobalDateTimeChange = newDateTime => {
        const utcDateTime = hasScheduleTemplatesFeature
            ? formatExpirationDate(newDateTime)
            : newDateTime?.utc().format();
        setGlobalExpirationDate(utcDateTime);
    };

    const handlePositiveValueChange = (rowID, TemplateId, attributeName, e) => {
        const rows = [...templateRows];
        const templateIneed = flowExportTradeDeskTemplateFields.find(x => x.FlowExportTemplateId == TemplateId);
        let positiveValue = 0;
        if (e.target.value != null) {
            let thisVal = e.target.value.replace(/[^0-9.]/g, "");

            if (thisVal > 0) {
                positiveValue = thisVal;
            }
        }
        if (attributeName == "CostPerMillion" && templateIneed.CostPerMillion == positiveValue) {
            return;
        }
        if (attributeName == "PercentOfMediaCost" && templateIneed.PercentOfMediaCost == positiveValue) {
            return;
        }
        //tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);
        if (attributeName == "CostPerMillion") {
            dispatch(
                updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, "IsAutoGeneratedCPM", false, true)
            );
            rows[rowID].IsAutoGeneratedCPM = false;
        } else {
            dispatch(
                updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, "IsAutoGeneratedPOM", false, true)
            );
            rows[rowID].IsAutoGeneratedPOM = false;
        }
        dispatch(updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, attributeName, positiveValue, true));
        setTemplateRows(rows);
        //  setRefreshTable(refreshTable + 1);
    };

    const handleGlobalPositiveValueChange = (attributeName, e) => {
        let positiveValue = 0;
        if (e.target.value != null) {
            let thisVal = e.target.value.replace(/[^0-9.]/g, "");

            if (thisVal > 0) {
                positiveValue = thisVal;
            }
        }

        if (attributeName == "PercentOfMediaCost") {
            setGlobalPercentOfMediaCost(positiveValue);
        } else {
            setGlobalCostPerMillion(positiveValue);
        }
    };

    const handleRateTypeChange = (TemplateId, value) =>
        dispatch(updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, "RateType", value));

    const handleExpansionTypeChange = (TemplateId, e) =>
        dispatch(updateAttribute("flowExportTradeDeskTemplateFields", TemplateId, "ExpansionType", e.target.value));

    const assignGlobalValues = () => {
        if (flowExportTradeDeskTemplateFields != null) {
            for (const template of flowExportTradeDeskTemplateFields) {
                // Set Expansion Type
                dispatch(
                    updateAttribute(
                        "flowExportTradeDeskTemplateFields",
                        template.FlowExportTemplateId,
                        "ExpansionType",
                        globalExpansionType
                    )
                );

                // Set Expiration Date
                dispatch(
                    updateAttribute(
                        "flowExportTradeDeskTemplateFields",
                        template.FlowExportTemplateId,
                        "ExpirationDate",
                        globalExpirationDate
                    )
                );

                if (DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType) {
                    // Set Percent Of Media Cost
                    dispatch(
                        updateAttribute(
                            "flowExportTradeDeskTemplateFields",
                            template.FlowExportTemplateId,
                            "PercentOfMediaCost",
                            globalPercentOfMediaCost
                        )
                    );

                    // Set Cost Per Million
                    dispatch(
                        updateAttribute(
                            "flowExportTradeDeskTemplateFields",
                            template.FlowExportTemplateId,
                            "CostPerMillion",
                            globalCostPerMillion
                        )
                    );
                    // Set IsAutoGenerated
                    dispatch(
                        updateAttribute(
                            "flowExportTradeDeskTemplateFields",
                            template.FlowExportTemplateId,
                            "IsAutoGeneratedCPM",
                            false
                        )
                    );
                    dispatch(
                        updateAttribute(
                            "flowExportTradeDeskTemplateFields",
                            template.FlowExportTemplateId,
                            "IsAutoGeneratedPOM",
                            false
                        )
                    );
                }
            }

            const rows = [...templateRows];
            for (let row of rows) {
                row.ExpansionType = globalExpansionType;
                row.ExpirationDate = globalExpirationDate;

                if (DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType) {
                    row.PercentOfMediaCost = globalPercentOfMediaCost;
                    row.CostPerMillion = globalCostPerMillion;
                    row.IsAutoGeneratedCPM = false;
                    row.IsAutoGeneratedPOM = false;
                }
            }

            setTemplateRows(rows);
            setRefreshTable(refreshTable + 1);
            dispatch(syncScheduleEndDateWithExpiration(globalExpirationDate));
        }
    };

    // re-attach MaterialTable's tableData.id (original row index) that the select* renderers expect
    const withId = row => ({ ...row.original, tableData: { id: row.index } });

    const columns = useMemo(() => {
        const thisDeliveryType = tapadDeliveryTypes.find(x => x.TapadDeliveryTypeId == tapadDeliveryTypeId);
        const headerStyle = { backgroundColor: "#0075bc", color: "#fff", fontWeight: 400, fontSize: "14px" };

        const columnData = [
            {
                accessorKey: "AudienceName",
                header: "Audience Name",
                cell: ({ row }) => selectAudienceName(withId(row)),
                size: 150,
                minSize: 80,
                meta: { headerStyle, width: 150 },
            },
            {
                accessorKey: "Description",
                header: "Description",
                cell: ({ row }) => selectDescription(withId(row)),
                size: 150,
                minSize: 80,
                meta: { headerStyle, width: 150 },
            },
            {
                accessorKey: "ExpansionType",
                header: "Expansion Type",
                cell: ({ row }) => selectExpansionType(withId(row)),
                size: 150,
                minSize: 80,
                meta: { headerStyle, width: 150 },
            },
            {
                accessorKey: "ExpirationDate",
                header: "Expiration Date",
                cell: ({ row }) => selectExpirationDate(withId(row)),
                size: 150,
                minSize: 80,
                meta: { headerStyle, width: 150 },
            },
        ];

        if (DeliveryType.ThirdPartyCustom == thisDeliveryType?.DeliveryType) {
            columnData.push(
                {
                    accessorKey: "RateType",
                    header: "Rate Type",
                    cell: ({ row }) => selectRateType(withId(row)),
                    size: 150,
                    minSize: 80,
                    meta: { headerStyle, width: 150 },
                },
                {
                    accessorKey: "CostPerMillion",
                    header: "CPM ($)",
                    cell: ({ row }) => selectCostPerMillion(withId(row)),
                    size: 150,
                    minSize: 80,
                    meta: { headerStyle, width: 150, cellStyle: getCellStyleCPM },
                },
                {
                    accessorKey: "PercentOfMediaCost",
                    header: "% of Media Cost",
                    cell: ({ row }) => selectPercentOfMediaCost(withId(row)),
                    size: 150,
                    minSize: 80,
                    meta: { headerStyle, width: 150, cellStyle: getCellStylePOM },
                },
                {
                    accessorKey: "ParentElementId",
                    header: "Parent Element Id",
                    cell: ({ row }) => selectParentElementId(withId(row)),
                    size: 150,
                    minSize: 80,
                    meta: { headerStyle, width: 150 },
                },
                {
                    accessorKey: "ParentElementName",
                    header: "Parent Element Name",
                    cell: ({ row }) => selectParentElementName(withId(row)),
                    size: 150,
                    minSize: 80,
                    meta: { headerStyle, width: 150 },
                }
            );
        }

        return columnData;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTable, deliveryType, canEdit, flow, tapadDeliveryTypeId, tapadDeliveryTypes]);

    const table = useReactTable({
        data: templateRows,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const theme = createTheme({
        overrides: {
            MuiTable: {
                root: {
                    marginTop: "0px !important",
                },
            },
            MuiTableCell: {
                root: {
                    fontSize: "14px",
                },
            },
        },
    });

    return (
        <>
            <div className="flow-edit-header" style={{ flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexGrow: 1 }}>
                    <p>Driver File Fields</p>
                </div>
                <div className="flow-edit-header-actions" style={{ display: "flex", marginLeft: "20px" }}>
                    <div style={{ display: "flex", marginLeft: "40px" }}></div>
                </div>
            </div>
            <Divider style={{ margin: "20px auto" }} />
            <ThemeProvider theme={theme}>
                <ListItem
                    key="button-global-fields-assignment"
                    style={{ display: "flex", justifyContent: "center" }}
                    button
                    onClick={() => setIsOpenGlobalAssignment(!isOpenGlobalAssignment)}
                >
                    <span style={{ display: "flex", fontSize: "16px" }}>Global Fields Assignment</span>
                    <span style={{ display: "flex", position: "absolute", right: "0", fontSize: "16px" }}>
                        {isOpenGlobalAssignment ? ExpandLess : ExpandMore}
                    </span>
                </ListItem>
                <Collapse
                    key="container-global-fields-assignment"
                    in={isOpenGlobalAssignment}
                    timeout="auto"
                    unmountOnExit
                >
                    <div className="row" style={{ height: "100%" }}>
                        <div className="col col-sm-4 col-md-3">
                            <FormControl fullWidth style={{ margin: "10px 0 0" }}>
                                <InputLabel shrink style={{ fontSize: 14 }}>
                                    Expansion Type
                                </InputLabel>
                                <Select
                                    value={globalExpansionType}
                                    onChange={e => setGlobalExpansionType(e.target.value)}
                                    disabled={!canEdit || (flow && flow.IsLocked)}
                                    displayEmpty
                                >
                                    <MenuItem key={2} value={"HOUSEHOLD"}>
                                        Household
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType ? (
                                <NumericalTextField
                                    label="% of Media Spend"
                                    numberType="percent"
                                    style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                    value={globalPercentOfMediaCost + "%"}
                                    disabled={!canEdit || (flow && flow.IsLocked)}
                                    onBlur={e => handleGlobalPositiveValueChange("PercentOfMediaCost", e)}
                                />
                            ) : null}
                        </div>
                        <div className="col col-sm-4 col-md-3">
                            {DeliveryType.ThirdPartyCustom == deliveryType?.DeliveryType ? (
                                <NumericalTextField
                                    label="CPM"
                                    numberType="currency3"
                                    style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                    value={truncateToThreeDecimals(globalCostPerMillion)}
                                    disabled={!canEdit || (flow && flow.IsLocked)}
                                    onBlur={e => handleGlobalPositiveValueChange("CostPerMillion", e)}
                                />
                            ) : null}
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                <DateTimePicker
                                    label="Expiration Date"
                                    style={{ fontSize: "14px", width: "100%", margin: "10px 0 0" }}
                                    format={"MMM DD, YYYY HH:mm"}
                                    value={globalExpirationDate}
                                    onChange={e => handleGlobalDateTimeChange(e)}
                                />
                            </MuiPickersUtilsProvider>
                        </div>
                        <div className="col col-sm-4 col-md-6" style={{ height: "100%" }}>
                            <Button
                                color="primary"
                                variant="contained"
                                style={{ position: "absolute", bottom: "0" }}
                                onClick={assignGlobalValues}
                            >
                                Assign
                            </Button>
                        </div>
                    </div>
                </Collapse>
                <TableContainer style={{ maxHeight: 400, margin: "20px 0px", width: "100%" }}>
                    <Table stickyHeader size="small" style={{ tableLayout: "fixed", width: "100%" }}>
                        <TableHead>
                            {table.getHeaderGroups().map(hg => (
                                <TableRow key={hg.id}>
                                    {hg.headers.map(header => {
                                        const meta = header.column.columnDef.meta || {};
                                        return (
                                            <TableCell
                                                key={header.id}
                                                style={{
                                                    ...meta.headerStyle,
                                                    width: header.getSize(),
                                                    position: "relative",
                                                }}
                                                sortDirection={header.column.getIsSorted() || false}
                                            >
                                                <TableSortLabel
                                                    active={!!header.column.getIsSorted()}
                                                    direction={header.column.getIsSorted() === "desc" ? "desc" : "asc"}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    style={{ color: "#fff" }}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </TableSortLabel>
                                                {header.column.getCanResize() && (
                                                    <div
                                                        onMouseDown={header.getResizeHandler()}
                                                        onTouchStart={header.getResizeHandler()}
                                                        onClick={e => e.stopPropagation()}
                                                        style={{
                                                            position: "absolute",
                                                            right: 0,
                                                            top: 0,
                                                            height: "100%",
                                                            width: 12,
                                                            cursor: "col-resize",
                                                            userSelect: "none",
                                                            touchAction: "none",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "#fff",
                                                            opacity: 0.6,
                                                        }}
                                                    >
                                                        ||
                                                    </div>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody>
                            {table.getRowModel().rows.map(row => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map(cell => {
                                        const meta = cell.column.columnDef.meta || {};
                                        const cellStyle = meta.cellStyle ? meta.cellStyle(null, row.original) : {};
                                        return (
                                            <TableCell
                                                key={cell.id}
                                                style={{
                                                    fontSize: 14,
                                                    padding: "14px 8px",
                                                    width: cell.column.getSize(),
                                                    ...cellStyle,
                                                }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ThemeProvider>
            <div id="templateFieldsTable" style={{ width: "100%", overflowX: "auto" }}></div>
            <Divider style={{ margin: "40px auto 20px auto" }} />
        </>
    );
};

ExportTradeDeskTemplateFields.propTypes = {
    tapadDeliveryTypeId: PropTypes.number,
    destinationId: PropTypes.number,
    flowExportTradeDeskTemplateFields: PropTypes.array,
    canEdit: PropTypes.bool,
    classes: PropTypes.any,
};

export default withStyles(styles)(ExportTradeDeskTemplateFields);
